/**
 * Speaker profiles -- group 04 (DSP-active monitors and North American passives).
 *
 * The group is chosen to separate four variables that usually travel together in
 * a catalogue: whether amplification and crossover are internal, whether the
 * low-frequency alignment is sealed or vented, whether the crossover is
 * high-order and minimum-phase or first-order and time-coherent, and whether the
 * design targets a flat on-axis response or a flat power response. Two entries
 * here are fully active with digital crossovers, one has a powered bass section
 * only, and the rest are passive with genuinely different filter orders.
 *
 * `responseTilt` is the authored voicing and is the field that carries each
 * design's character; the baseline rolloff, impedance and directivity are
 * derived by the kit from the published alignment. Nothing is MEASURED.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_04: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Dutch & Dutch',
    model: '8c',
    price: usd(14500, 'APPROXIMATE', 2023, 'Approximate US retail per pair; sold largely direct and priced in euro at source, so this is a recalled converted figure.'),
    year: 2016,
    country: 'Netherlands',
    speakerType: 'active',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    active: true,
    sensitivityDb: 88,
    nominalOhms: 8,
    minOhms: 7.6,
    bass: [30, 25],
    hfExtension: 22000,
    crossovers: [
      { freqHz: 100, slopeDbOct: 24, alignment: 'digital-lr' },
      { freqHz: 1250, slopeDbOct: 24, alignment: 'digital-lr' },
    ],
    responseTilt: [[20, 0], [45, 0.2], [110, -0.3], [400, 0], [1200, 0.1], [3000, 0.2], [8000, -0.2], [14000, -0.4], [20000, -0.9]],
    resonances: [[190, -42, 12], [640, -46, 16]],
    coverageAngleDeg: 90,
    dimensionsMm: [380, 260, 380],
    massKg: 25,
    thdPercent: 0.3,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#e07a5f',
    tags: ['active', 'dsp-crossover', 'cardioid', 'studio-monitor', 'wall-mount-target'],
    notes:
      'The defining behaviour is a cardioid midrange: two rear-mounted woofers in a resistively vented chamber cancel rearward radiation from roughly 100 Hz to 1 kHz, so the sidewall and front-wall reflection energy is far lower than the on-axis response alone would suggest. Above the 1250 Hz crossover the tweeter is loaded by a waveguide that continues the same directivity, which is why the modelled coverage angle is held at 90 degrees rather than widening through the midrange as a conventional two-way does. Because the crossover is digital and the amplification internal, the impedance entry here is a placeholder and the amplifier profile upstream contributes nothing to this speaker.',
    provenanceNote:
      'Driver complement, crossover frequencies, cardioid operating range and the active DSP architecture are from Dutch and Dutch published material. Bass extension, sensitivity and cabinet resonances are ESTIMATED; the sensitivity and impedance values in particular are placeholders required by the schema and have no physical meaning for a powered design with a line-level input. Anechoic response shape and directivity are modelled.',
  }),

  makeSpeaker({
    mfr: 'Grimm Audio',
    model: 'LS1be',
    price: usd(30000, 'APPROXIMATE', 2023, 'Approximate US retail per pair including the required electronics; priced in euro at source, so this is a recalled converted figure.'),
    year: 2015,
    country: 'Netherlands',
    speakerType: 'active',
    enclosure: 'sealed',
    drivers: ['beryllium-dome', 'dynamic'],
    driverCount: 3,
    wayCount: 2,
    active: true,
    sensitivityDb: 86,
    nominalOhms: 8,
    minOhms: 7.6,
    bass: [42, 34],
    hfExtension: 40000,
    crossovers: [{ freqHz: 1500, slopeDbOct: 6, alignment: 'first-order' }],
    responseTilt: [[20, 0], [60, 0.3], [150, -0.2], [500, 0], [1500, -0.2], [4000, -0.3], [9000, -0.5], [15000, -0.8], [20000, -1.2]],
    resonances: [[230, -44, 13], [520, -48, 18]],
    coverageAngleDeg: 110,
    dimensionsMm: [1100, 240, 300],
    massKg: 22,
    thdPercent: 0.25,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#6a8f9c',
    tags: ['active', 'first-order-crossover', 'time-coherent', 'beryllium', 'mastering'],
    notes:
      'Where the Dutch and Dutch entry uses a steep digital Linkwitz-Riley alignment, this design deliberately runs a first-order acoustic crossover so that the summed step response stays coherent, and accepts the consequences: wide driver overlap, both drivers producing significant output an octave either side of 1.5 kHz, and correspondingly higher excursion demands on the tweeter. That choice is what the profile encodes -- a 6 dB/octave first-order section instead of a 24 dB/octave one -- and it changes the through-crossover phase behaviour and vertical lobing far more than the on-axis magnitude. The sealed alignment plus a modest 42 Hz corner also means room gain is being relied on for the bottom octave rather than a port.',
    provenanceNote:
      'The first-order crossover philosophy, active architecture and beryllium tweeter are from Grimm Audio published material. Crossover frequency, bass extension, sensitivity, dimensions and mass are ESTIMATED; Grimm publishes design intent in far more detail than it publishes numbers. Sensitivity and impedance are schema placeholders and have no meaning for a powered design.',
  }),

  makeSpeaker({
    mfr: 'Buchardt Audio',
    model: 'S400 MKII',
    price: usd(2499, 'APPROXIMATE', 2023, 'Approximate US retail per pair, sold direct; recalled figure.'),
    year: 2021,
    country: 'Denmark',
    speakerType: 'standmount',
    enclosure: 'passive-radiator',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 88,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [33, 28],
    hfExtension: 24000,
    portTuningHz: 32,
    crossovers: [[2000, 12]],
    responseTilt: [[20, 0], [60, 0.6], [120, 0.3], [400, -0.3], [1200, -0.6], [2500, -0.4], [5000, 0.4], [10000, 0.3], [20000, -0.9]],
    resonances: [[210, -32, 9], [560, -36, 12]],
    coverageAngleDeg: 120,
    recommendedWatts: [30, 200],
    dimensionsMm: [380, 180, 240],
    massKg: 9.6,
    thdPercent: 0.4,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#9c8f7a',
    tags: ['passive-radiator', 'waveguide-tweeter', 'compact', 'direct-sale'],
    notes:
      'A rear-firing passive radiator replaces the port, which removes organ-pipe resonance and port compression from the model entirely while keeping the fourth-order rolloff of a vented alignment -- so the low-frequency behaviour is that of a reflex box without the chuffing and midrange leakage terms. The passive radiator is a mass-loaded diaphragm with its own excursion limit, so the profile keeps the vented rolloff order but pairs it with a comparatively low excursion knee. The tweeter sits in a shallow waveguide, which is why the voicing carries a broad shallow dip through the presence region rather than the on-axis rise a free-standing dome would produce.',
    provenanceNote:
      'Passive-radiator alignment, driver complement, nominal impedance and cabinet dimensions are from Buchardt published material. Crossover frequency, bass extension, sensitivity, minimum impedance and cabinet resonances are ESTIMATED. The voicing offsets are authored to match the described waveguide loading and are modelled, not measured.',
  }),

  makeSpeaker({
    mfr: 'Revel',
    model: 'Ultima2 Salon2',
    price: usd(21998, 'APPROXIMATE', 2019, 'Approximate US retail per pair while current; recalled figure.'),
    year: 2008,
    country: 'United States',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['beryllium-dome', 'dynamic'],
    driverCount: 6,
    wayCount: 4,
    sensitivityDb: 86,
    nominalOhms: 6,
    minOhms: 3.7,
    bass: [23, 18],
    hfExtension: 45000,
    portTuningHz: 24,
    crossovers: [[150, 24], [575, 24], [2300, 24]],
    responseTilt: [[20, 0], [50, 0.2], [120, -0.2], [350, 0.1], [900, 0], [2300, 0.1], [5000, -0.1], [10000, 0.2], [20000, -0.5]],
    resonances: [[160, -44, 16], [430, -48, 20]],
    coverageAngleDeg: 105,
    recommendedWatts: [50, 400],
    dimensionsMm: [1321, 305, 470],
    massKg: 66,
    thdPercent: 0.2,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#7a8c9c',
    tags: ['four-way', 'beryllium', 'flat-on-axis', 'controlled-directivity', 'harman-lineage'],
    notes:
      'This is the flattest voicing in the group by design: the development programme targeted a flat on-axis response and a smoothly falling, resonance-free off-axis family, so the authored tilt stays inside a few tenths of a dB everywhere rather than carrying a house character. Four ways with 24 dB/octave sections means each driver operates over a narrow band well inside its pistonic range, which is why the modelled distortion figure is the lowest of the passive entries here. The 3.7 ohm minimum on a nominal 6 ohm rating makes it a genuine current load, so a low-damping-factor amplifier upstream will show audible response modulation on this profile.',
    provenanceNote:
      'Crossover frequencies, driver complement, sensitivity, nominal and minimum impedance, bass extension, dimensions and mass are from Revel published specifications. The anechoic response shape, cabinet resonances and directivity model are MODELLED from the stated four-way alignment and the published design target; they are not transcribed measurement data.',
  }),

  makeSpeaker({
    mfr: 'JBL',
    model: 'Everest DD67000',
    price: usd(80000, 'APPROXIMATE', 2023, 'Approximate US retail per pair; recalled figure and pricing has been revised several times over the production run.'),
    year: 2011,
    country: 'United States',
    speakerType: 'horn',
    enclosure: 'ported',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 96,
    nominalOhms: 8,
    minOhms: 3.2,
    bass: [30, 25],
    hfExtension: 40000,
    portTuningHz: 29,
    crossovers: [[700, 12], [15000, 12]],
    responseTilt: [[20, 0], [50, 0.8], [120, 0.2], [350, -0.6], [800, 0.7], [2000, 1.1], [4500, 0.4], [9000, -0.8], [16000, 0.6], [20000, -0.4]],
    resonances: [[135, -34, 10], [400, -38, 13], [1700, -40, 18]],
    coverageAngleDeg: 70,
    recommendedWatts: [10, 400],
    dimensionsMm: [1000, 1180, 470],
    massKg: 141,
    thdPercent: 0.5,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#c9a05a',
    tags: ['horn', 'compression-driver', 'high-sensitivity', 'dual-15-inch', 'bi-radial'],
    notes:
      'Two 15-inch woofers in a vented box hand over at 700 Hz to a large-format compression driver on a bi-radial horn, so above that point the source is a directivity-controlled device and below it a pair of direct radiators on a very wide baffle -- the profile keeps a narrow 70 degree coverage angle to represent that. At 96 dB per 2.83 V the noise floor of everything upstream becomes an audible term, which is the main system-level consequence of this entry. Distortion is given the horn shape rather than the direct-radiator shape because the dominant mechanism above 700 Hz is air nonlinearity in the horn throat, which grows with SPL faster than cone distortion does.',
    provenanceNote:
      'Sensitivity, driver complement, crossover frequencies, nominal impedance, dimensions and mass are from JBL published specifications. Minimum impedance, port tuning, bass extension and cabinet resonances are ESTIMATED. The voicing offsets represent the horn and baffle behaviour described in JBL technical material and are modelled, not measured.',
  }),

  makeSpeaker({
    mfr: 'Paradigm',
    model: 'Persona 3F',
    price: usd(10998, 'APPROXIMATE', 2022, 'Approximate US retail per pair; recalled figure.'),
    year: 2017,
    country: 'Canada',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['beryllium-dome', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 92,
    nominalOhms: 8,
    minOhms: 4.0,
    bass: [30, 24],
    hfExtension: 45000,
    portTuningHz: 29,
    crossovers: [[400, 24], [2400, 24]],
    responseTilt: [[20, 0], [55, 0.4], [130, -0.4], [500, -0.2], [1500, 0.3], [3500, 0.5], [7000, 0.8], [12000, 0.9], [20000, 0.3]],
    resonances: [[175, -40, 14], [480, -44, 18]],
    coverageAngleDeg: 100,
    recommendedWatts: [15, 300],
    dimensionsMm: [1130, 300, 400],
    massKg: 32,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#b8607a',
    tags: ['beryllium-midrange', 'beryllium-tweeter', 'perforated-phase-alignment', 'ported'],
    notes:
      'Beryllium is used for the midrange cone as well as the tweeter dome, which pushes the first cone breakup of the midrange far above its 2400 Hz upper crossover instead of just outside it -- so the profile carries no midband breakup resonance and its cabinet resonances are the only stored-energy terms below 1 kHz. The consequence of two very stiff, very light diaphragms is an on-axis response that rises gently through the top two octaves rather than falling, which is what the authored voicing encodes and what most clearly separates this entry from the Revel one at a similar price. The perforated lens in front of each driver suppresses the breakup mode that survives, at the cost of a small broadband insertion loss.',
    provenanceNote:
      'Driver materials, crossover frequencies, sensitivity, nominal impedance, dimensions and mass are from Paradigm published specifications. Minimum impedance, port tuning, bass extension and cabinet resonances are ESTIMATED. The rising treble voicing is authored from the published design description and from beryllium diaphragm behaviour; it is MODELLED, not measured.',
  }),

  makeSpeaker({
    mfr: 'Totem Acoustic',
    model: 'Mani-2 Signature',
    price: usd(5500, 'APPROXIMATE', 2022, 'Approximate US retail per pair; recalled figure.'),
    year: 2013,
    country: 'Canada',
    speakerType: 'standmount',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 2,
    sensitivityDb: 85,
    nominalOhms: 4,
    minOhms: 2.6,
    bass: [29, 24],
    hfExtension: 22000,
    crossovers: [[2200, 12]],
    responseTilt: [[20, 0], [55, -0.4], [140, -0.5], [450, 0.2], [1200, 0.5], [2800, 0.6], [6000, 0.2], [11000, -0.3], [20000, -1.3]],
    resonances: [[200, -38, 11], [540, -42, 15]],
    coverageAngleDeg: 125,
    recommendedWatts: [50, 250],
    dimensionsMm: [432, 216, 305],
    massKg: 20,
    thdPercent: 0.45,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#8a6f9c',
    tags: ['isobaric', 'sealed', 'difficult-load', 'compact-floor-extension'],
    notes:
      'Two woofers are mounted isobarically -- one behind the other sharing a trapped air volume -- which halves the effective compliance the box has to provide and lets a 29 Hz sealed corner come out of a cabinet this size, at the cost of twice the moving mass, twice the voice-coil current and roughly 3 dB of sensitivity. That is why the profile pairs an 85 dB sensitivity with a 2.6 ohm minimum on a nominal 4 ohm rating: it is the hardest amplifier load in this group by a wide margin and will visibly modulate the response of any low-feedback or tube amplifier upstream. The sealed second-order rolloff also means the bottom octave leans on room gain rather than a port, so room coupling is set low and the group delay below 60 Hz stays small.',
    provenanceNote:
      'The isobaric woofer arrangement, sealed alignment, nominal impedance, sensitivity and cabinet dimensions are from Totem Acoustic published material. Minimum impedance, crossover frequency, bass extension and cabinet resonances are ESTIMATED from the described alignment and from norms for isobaric standmounts. The voicing is authored and MODELLED.',
  }),

  makeSpeaker({
    mfr: 'Vandersteen Audio',
    model: 'Quatro Wood CT',
    price: usd(15800, 'APPROXIMATE', 2022, 'Approximate US retail per pair; recalled figure.'),
    year: 2015,
    country: 'United States',
    speakerType: 'floorstander',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 4,
    active: true,
    sensitivityDb: 85,
    nominalOhms: 8,
    minOhms: 6.0,
    bass: [22, 18],
    hfExtension: 30000,
    crossovers: [
      { freqHz: 100, slopeDbOct: 6, alignment: 'first-order' },
      { freqHz: 900, slopeDbOct: 6, alignment: 'first-order' },
      { freqHz: 5000, slopeDbOct: 6, alignment: 'first-order' },
    ],
    responseTilt: [[20, 0], [45, 0.3], [110, 0.2], [350, -0.2], [1000, -0.3], [2500, -0.5], [5000, -0.6], [10000, -0.9], [20000, -1.6]],
    resonances: [[150, -40, 10], [420, -44, 14]],
    coverageAngleDeg: 115,
    recommendedWatts: [40, 250],
    dimensionsMm: [1118, 254, 381],
    massKg: 50,
    thdPercent: 0.35,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#7f6a52',
    tags: ['first-order', 'time-and-phase-aligned', 'powered-bass', 'analog-room-eq', 'stepped-baffle'],
    notes:
      'Every section is first-order and the drivers are physically stepped back down the baffle so their acoustic centres line up, which makes the summed step response a single coherent event rather than the separate arrivals a high-order crossover produces -- the three 6 dB/octave sections in this profile are the whole point of the design. The bottom section is amplified internally and fed through an eleven-band analog equaliser intended to be set for the room, so the upstream amplifier only ever sees the passive mid and treble sections and the bass alignment is effectively decoupled from it. Because the crossovers are shallow the drivers overlap by two octaves or more, so vertical listening height matters far more on this profile than on the 24 dB/octave designs in the same group.',
    provenanceNote:
      'The first-order crossover philosophy, time-and-phase-aligned driver layout, powered bass section with analog room equalisation, sensitivity, nominal impedance, dimensions and mass are from Vandersteen published material. Crossover frequencies, minimum impedance, bass extension and cabinet resonances are ESTIMATED. The gently falling voicing is authored to reflect the first-order overlap and stepped baffle and is MODELLED.',
  }),
];
